import { supabase } from '@/lib/supabase'
import type {
  CommunityReaction,
  CommunityMessageAttachment,
  CommunityThread,
} from '@/modules/comunidade/services/communityService'
import { aggregateCardReactions } from '@/modules/comunidade/services/communityService'
import { prepareCommunityMessageContent } from '@/modules/comunidade/utils/communityMessageContent'

export type AnalysisCommunitySummary = {
  analysisId: number
  messagesCount: number
  lastMessageAt: string | null
  avatars: {
    authorId: string
    authorName: string
    authorAvatarUrl: string | null
  }[]
}

export type AnalysisCommunitySummaryMap = Record<string, AnalysisCommunitySummary>

export function sanitizeCommunityMessageContent(input: string): string {
  return String(prepareCommunityMessageContent(input).text || '')
}

export async function fetchAnalysisCommunitySummary(ids: number[]): Promise<AnalysisCommunitySummaryMap> {
  const uniqueIds = Array.from(
    new Set(
      (ids || [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id)),
    ),
  )
  if (!uniqueIds.length) return {}

  const { data, error } = await supabase.rpc('analysis_community_summary' as any, {
    p_analysis_ids: uniqueIds,
  })

  if (error) {
    // eslint-disable-next-line no-console
    console.error('analysis_community_summary error', error)
    return {}
  }

  const map: AnalysisCommunitySummaryMap = {}
  const rows = Array.isArray(data) ? data : []

  // Coleta todos os autores presentes no resumo para aplicar override via profiles
  const avatarAuthorIds = new Set<string>()
  rows.forEach((row: any) => {
    const avatarsJson = row.avatars as any
    if (!Array.isArray(avatarsJson)) return
    avatarsJson.forEach((a: any) => {
      const id = String(a.author_id || a.authorId || '').trim()
      if (id) avatarAuthorIds.add(id)
    })
  })

  const profilesMap: Record<
    string,
    {
      name: string
      avatarUrl: string | null
    }
  > = {}

  if (avatarAuthorIds.size > 0) {
    try {
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles' as any)
        .select('id,full_name,avatar_url')
        .in('id', Array.from(avatarAuthorIds) as any)

      if (profilesError) {
        // eslint-disable-next-line no-console
        console.error('fetchAnalysisCommunitySummary profiles error', profilesError)
      } else if (Array.isArray(profiles)) {
        profiles.forEach((p: any) => {
          const id = p.id ? String(p.id) : ''
          if (!id) return
          profilesMap[id] = {
            name: String(p.full_name || '').trim(),
            avatarUrl:
              typeof p.avatar_url === 'string' || p.avatar_url === null
                ? (p.avatar_url as string | null)
                : null,
          }
        })
      }
    } catch {
      // Em caso de erro, usamos apenas os snapshots da função SQL
    }
  }

  rows.forEach((row: any) => {
    const id = Number(row.analysis_id)
    if (!Number.isFinite(id)) return

    const messagesCount = Number(row.messages_count ?? 0)
    const lastMessageAt = row.last_message_at ? String(row.last_message_at) : null
    const avatarsJson = row.avatars as any

    const avatars =
      Array.isArray(avatarsJson) &&
      avatarsJson.map((a: any) => {
        const authorId = String(a.author_id || a.authorId || '').trim()
        const profileOverride = profilesMap[authorId]

        const overrideName = profileOverride?.name?.trim()
        const rawName =
          (overrideName && overrideName.length > 0 ? overrideName : null) ||
          (a.author_name as string | null) ||
          (a.authorName as string | null) ||
          null

        let authorAvatarUrl: string | null =
          typeof a.author_avatar_url === 'string' || a.author_avatar_url === null
            ? (a.author_avatar_url as string | null)
            : null

        if (profileOverride && profileOverride.avatarUrl !== undefined) {
          authorAvatarUrl = profileOverride.avatarUrl
        }

        return {
          authorId,
          authorName: String(rawName || '').trim() || 'Usuário',
          authorAvatarUrl,
        }
      })

    map[String(id)] = {
      analysisId: id,
      messagesCount,
      lastMessageAt,
      avatars: Array.isArray(avatars) ? avatars : [],
    }
  })

  return map
}

export async function fetchAnalysisCommunityThread(analysisId: number): Promise<CommunityThread> {
  const id = Number(analysisId)
  if (!Number.isFinite(id)) {
    return { messages: [], cardReactions: [] }
  }

  let currentUserId: string | null = null
  try {
    const auth = await supabase.auth.getUser()
    const user = auth.data.user
    currentUserId = user?.id ? String(user.id) : null
  } catch {
    currentUserId = null
  }

  const { data, error } = await supabase
    .from('analysis_community_messages' as any)
    .select(
      `
        id,
        analysis_id,
        author_id,
        author_name,
        author_avatar_url,
        content,
        created_at,
        reactions:analysis_community_message_reactions(message_id,user_id,emoji,created_at),
        attachments:analysis_community_message_attachments(id,message_id,url,storage_bucket,storage_path,content_type,size_bytes,created_at)
      `,
    )
    .eq('analysis_id', id)
    .order('created_at', { ascending: true })

  if (error) {
    // eslint-disable-next-line no-console
    console.error('fetchAnalysisCommunityThread error', error)
    return { messages: [], cardReactions: [] }
  }

  const rows = Array.isArray(data) ? data : []
  let cardReactions: CommunityReaction[] = []

  try {
    const { data: cardRows, error: cardError } = await supabase
      .from('analysis_community_reactions' as any)
      .select('analysis_id,user_id,emoji')
      .eq('analysis_id', id)

    if (cardError) {
      // eslint-disable-next-line no-console
      console.error('fetchAnalysisCommunityThread card reactions error', cardError)
    } else {
      cardReactions = aggregateCardReactions(cardRows, currentUserId)
    }
  } catch (cardUnexpectedError) {
    // eslint-disable-next-line no-console
    console.error('fetchAnalysisCommunityThread card reactions unexpected error', cardUnexpectedError)
  }

  const messages = rows.map((row: any) => {
    const attachmentsRaw = Array.isArray(row.attachments) ? row.attachments : []
    const reactionsRaw = Array.isArray(row.reactions) ? row.reactions : []

    const attachments: CommunityMessageAttachment[] = attachmentsRaw.map((img: any) => ({
      id: Number(img.id),
      messageId: Number(img.message_id),
      url: String(img.url || ''),
      storageBucket:
        typeof img.storage_bucket === 'string' || img.storage_bucket === null
          ? (img.storage_bucket as string | null)
          : null,
      storagePath:
        typeof img.storage_path === 'string' || img.storage_path === null
          ? (img.storage_path as string | null)
          : null,
      contentType:
        typeof img.content_type === 'string' || img.content_type === null
          ? (img.content_type as string | null)
          : null,
      sizeBytes:
        img.size_bytes === null || img.size_bytes === undefined
          ? null
          : Number.isFinite(Number(img.size_bytes))
            ? Number(img.size_bytes)
            : null,
      createdAt: img.created_at ? String(img.created_at) : '',
    }))

    const reactionsMap: Record<string, CommunityReaction> = {}
    reactionsRaw.forEach((rec: any) => {
      const emoji = String(rec.emoji || '').trim()
      if (!emoji) return
      let agg = reactionsMap[emoji]
      if (!agg) {
        agg = { emoji, count: 0, reactedByCurrentUser: false }
        reactionsMap[emoji] = agg
      }
      agg.count += 1
      if (currentUserId && String(rec.user_id) === currentUserId) {
        agg.reactedByCurrentUser = true
      }
    })

    const reactions = Object.values(reactionsMap).sort((a, b) => b.count - a.count)

    return {
      id: Number(row.id),
      opportunityId: Number(row.analysis_id),
      authorId: row.author_id ? String(row.author_id) : '',
      authorName: String(row.author_name || '').trim() || 'Usuário',
      authorAvatarUrl:
        typeof row.author_avatar_url === 'string' || row.author_avatar_url === null
          ? (row.author_avatar_url as string | null)
          : null,
      content: prepareCommunityMessageContent(String(row.content || '')).html,
      createdAt: row.created_at ? String(row.created_at) : '',
      reactions,
      attachments,
    }
  })

  return {
    messages,
    cardReactions,
  }
}

export async function postAnalysisCommunityMessage(
  analysisId: number,
  author: { name: string; avatarUrl?: string | null },
  content: string,
): Promise<number | null> {
  const id = Number(analysisId)
  if (!Number.isFinite(id)) return null

  const prepared = prepareCommunityMessageContent(content)

  const auth = await supabase.auth.getUser()
  const user = auth.data.user
  const userId = user?.id
  if (!userId) throw new Error('Usuário não autenticado')

  const authorName = String(author.name || '').trim() || 'Usuário'
  const authorAvatarUrl =
    typeof author.avatarUrl === 'string' || author.avatarUrl === null
      ? (author.avatarUrl as string | null)
      : null

  const { data, error } = await supabase
    .from('analysis_community_messages' as any)
    .insert({
      analysis_id: id,
      author_id: userId,
      author_name: authorName,
      author_avatar_url: authorAvatarUrl,
      content: prepared.html,
    } as any)
    .select('id')
    .single()

  if (error) {
    // eslint-disable-next-line no-console
    console.error('postAnalysisCommunityMessage error', error)
    throw error
  }

  return Number((data as any).id)
}

export async function addAnalysisMessageAttachments(
  messageId: number,
  items: {
    url: string
    storageBucket?: string | null
    storagePath?: string | null
    contentType?: string | null
    sizeBytes?: number | null
  }[],
): Promise<CommunityMessageAttachment[]> {
  const id = Number(messageId)
  if (!Number.isFinite(id) || id <= 0) return []

  const rows = (items || [])
    .map((item) => {
      const url = String(item.url || '').trim()
      if (!url) return null
      return {
        message_id: id,
        url,
        storage_bucket: item.storageBucket ?? null,
        storage_path: item.storagePath ?? null,
        content_type: item.contentType ?? null,
        size_bytes:
          typeof item.sizeBytes === 'number' && Number.isFinite(item.sizeBytes)
            ? Math.max(0, Math.floor(item.sizeBytes))
            : item.sizeBytes == null
              ? null
              : Number(item.sizeBytes) || null,
      }
    })
    .filter(Boolean) as any[]

  if (!rows.length) return []

  const { data, error } = await supabase
    .from('analysis_community_message_attachments' as any)
    .insert(rows as any)
    .select('id,message_id,url,storage_bucket,storage_path,content_type,size_bytes,created_at')

  if (error) {
    // eslint-disable-next-line no-console
    console.error('addAnalysisMessageAttachments error', error)
    throw error
  }

  const out: CommunityMessageAttachment[] = Array.isArray(data)
    ? (data as any[]).map((row) => ({
        id: Number(row.id),
        messageId: Number(row.message_id),
        url: String(row.url || ''),
        storageBucket:
          typeof row.storage_bucket === 'string' || row.storage_bucket === null
            ? (row.storage_bucket as string | null)
            : null,
        storagePath:
          typeof row.storage_path === 'string' || row.storage_path === null
            ? (row.storage_path as string | null)
            : null,
        contentType:
          typeof row.content_type === 'string' || row.content_type === null
            ? (row.content_type as string | null)
            : null,
        sizeBytes:
          row.size_bytes === null || row.size_bytes === undefined
            ? null
            : Number.isFinite(Number(row.size_bytes))
              ? Number(row.size_bytes)
              : null,
        createdAt: row.created_at ? String(row.created_at) : '',
      }))
    : []

  return out
}

export async function toggleAnalysisMessageReaction(messageId: number, emoji: string): Promise<void> {
  const trimmed = String(emoji || '').trim()
  if (!trimmed) return

  const auth = await supabase.auth.getUser()
  const userId = auth.data.user?.id
  if (!userId) throw new Error('UsuÇ­rio nÇœo autenticado')

  const id = Number(messageId)
  if (!Number.isFinite(id) || id <= 0) return

  const { data, error } = await supabase
    .from('analysis_community_message_reactions' as any)
    .select('message_id,user_id,emoji')
    .eq('message_id', id)
    .eq('user_id', userId)
    .eq('emoji', trimmed)
    .maybeSingle()

  if (error && String((error as any).code || '') !== 'PGRST116') {
    // eslint-disable-next-line no-console
    console.error('toggleAnalysisMessageReaction select error', error)
  }

  if (data) {
    const { error: delError } = await supabase
      .from('analysis_community_message_reactions' as any)
      .delete()
      .match({ message_id: id, user_id: userId, emoji: trimmed } as any)
    if (delError) {
      // eslint-disable-next-line no-console
      console.error('toggleAnalysisMessageReaction delete error', delError)
    }
    return
  }

  const { error: insError } = await supabase
    .from('analysis_community_message_reactions' as any)
    .insert({ message_id: id, user_id: userId, emoji: trimmed } as any)

  if (insError) {
    // eslint-disable-next-line no-console
    console.error('toggleAnalysisMessageReaction insert error', insError)
  }
}

export async function fetchAnalysisCardReactions(
  analysisId: number,
): Promise<CommunityReaction[]> {
  const id = Number(analysisId)
  if (!Number.isFinite(id)) return []

  let currentUserId: string | null = null
  try {
    const auth = await supabase.auth.getUser()
    const user = auth.data.user
    currentUserId = user?.id ? String(user.id) : null
  } catch {
    currentUserId = null
  }

  const { data, error } = await supabase
    .from('analysis_community_reactions' as any)
    .select('analysis_id,user_id,emoji')
    .eq('analysis_id', id)

  if (error) {
    // eslint-disable-next-line no-console
    console.error('fetchAnalysisCardReactions error', error)
    return []
  }

  return aggregateCardReactions(data, currentUserId)
}

export async function toggleAnalysisCardReaction(analysisId: number, emoji: string): Promise<void> {
  const id = Number(analysisId)
  if (!Number.isFinite(id)) return
  const symbol = String(emoji || '').trim()
  if (!symbol) return

  const auth = await supabase.auth.getUser()
  const user = auth.data.user
  const userId = user?.id
  if (!userId) throw new Error('Usuário não autenticado')

  const { data, error } = await supabase
    .from('analysis_community_reactions' as any)
    .select('id')
    .eq('analysis_id', id)
    .eq('user_id', userId)
    .eq('emoji', symbol)
    .maybeSingle()

  if (error && (error as any).code !== 'PGRST116') {
    // eslint-disable-next-line no-console
    console.error('toggleAnalysisCardReaction error', error)
    throw error
  }

  if (data) {
    const delError = await supabase
      .from('analysis_community_reactions' as any)
      .delete()
      .eq('id', (data as any).id)
    if ((delError as any).error) {
      // eslint-disable-next-line no-console
      console.error('toggleAnalysisCardReaction delete error', (delError as any).error)
    }
    return
  }

  const insError = await supabase
    .from('analysis_community_reactions' as any)
    .insert({
      analysis_id: id,
      user_id: userId,
      emoji: symbol,
    } as any)

  if ((insError as any).error) {
    // eslint-disable-next-line no-console
    console.error('toggleAnalysisCardReaction insert error', (insError as any).error)
  }
}

export async function deleteAnalysisCommunityMessage(messageId: number): Promise<void> {
  const id = Number(messageId)
  if (!Number.isFinite(id) || id <= 0) return

  const { error } = await supabase
    .from('analysis_community_messages' as any)
    .delete()
    .eq('id', id)

  if (error) {
    // eslint-disable-next-line no-console
    console.error('deleteAnalysisCommunityMessage error', error)
  }
}

export async function updateAnalysisCommunityMessage(
  messageId: number,
  content: string,
): Promise<string | null> {
  const id = Number(messageId)
  if (!Number.isFinite(id) || id <= 0) return null

  const prepared = prepareCommunityMessageContent(content)
  if (!prepared.text) return null

  const { data, error } = await supabase
    .from('analysis_community_messages' as any)
    .update({ content: prepared.html } as any)
    .eq('id', id)
    .select('content')
    .single()

  if (error) {
    // eslint-disable-next-line no-console
    console.error('updateAnalysisCommunityMessage error', error)
    return null
  }

  return String((data as any).content || prepared.html || '')
}
